<script lang="ts" setup>
import { QuestionFilled } from "@element-plus/icons-vue";
import { computed, onMounted, ref } from "vue";
import NotificationPanel from "./NotificationPanel.vue";
import { useReminderStore } from "~/stores/ReminderStore";

withDefaults(
  defineProps<{
    /**
     * Support Tickets path for whichever site this is mounted on. Note that the
     * `applicationRouteMap.*.path` values already end in a trailing slash, e.g.
     * `${applicationRouteMap.Compliance.path}support-tickets` or
     * `${applicationRouteMap.HMB.path}support-tickets`.
     */
    to?: string;
  }>(),
  {
    to: "/support-tickets",
  },
);

const reminderStore = useReminderStore();
const popoverVisible = ref(false);

/** Unread SLA reminders for support tickets (response/fix due, overdue, escalated). */
const unreadCount = computed(() => reminderStore.supportUnreadCount);

const tooltip = computed(() =>
  unreadCount.value === 0
    ? "Help & Support"
    : `Help & Support — ${unreadCount.value} ticket ${
        unreadCount.value === 1 ? "reminder" : "reminders"
      }`,
);

onMounted(() => {
  // Shared with the notification bell; only the first mount actually fetches.
  if (!reminderStore.loaded) {
    reminderStore.load();
  }
});
</script>

<template>
  <!--
    Click opens the ticket-reminder panel (mirroring the notification bell) rather than
    navigating straight to the tickets page — the panel's "View tickets" link and every
    reminder row still lead there.
  -->
  <el-popover
    v-model:visible="popoverVisible"
    placement="bottom-end"
    trigger="click"
    :width="380"
    :show-arrow="false"
    popper-class="help-support-popover"
  >
    <template #reference>
      <span
        class="help-support"
        role="button"
        tabindex="0"
        :aria-label="tooltip"
        @keydown.enter.prevent="popoverVisible = !popoverVisible"
        @keydown.space.prevent="popoverVisible = !popoverVisible"
      >
        <el-tooltip :content="tooltip" placement="bottom" :show-after="300">
          <el-badge
            :value="unreadCount"
            :hidden="unreadCount === 0"
            :max="99"
            class="help-support-badge"
          >
            <el-icon :size="20" class="help-support-icon">
              <QuestionFilled />
            </el-icon>
          </el-badge>
        </el-tooltip>
      </span>
    </template>
    <NotificationPanel
      variant="support"
      :support-tickets-path="to"
      @close="popoverVisible = false"
    />
  </el-popover>
</template>

<style lang="less" scoped>
.help-support {
  cursor: pointer;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;

  &-badge {
    display: flex;
    align-items: center;
  }

  &-icon {
    color: var(--el-text-color-regular);
    transition: color 0.15s;

    &:hover {
      color: var(--el-color-primary);
    }
  }
}
</style>
